import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import useRoutes from "../../../store/routes";
import Modal from "../../../components/modal";
import CreateRouteBeartrack from "../../../components/createRouteBeartrack/createRouteBeartrack";
import BlockDeleteUserBeartrack from "../../../components/blockDeleteUserBeartrack";
import EditRouteClients from "../../../components/editRouteClients";
import SearchDropdown from "../../../components/searchDropdown/searchDropdown";
import useAuth from "../../../hooks/useAuth";
import useIsMobile from "../../../hooks/useIsMobile";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Button } from "../../../components/ui/Button";
import { Skeleton } from "../../../components/ui/Skeleton";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { EmptyState } from "../../../components/ui/EmptyState";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Route,
  Calendar,
  Layers,
  Users,
  User,
  UserX,
  X,
  ArrowUpDown,
} from "lucide-react";
import "./adminWithdrawalsModern.scss";
import { useTranslation } from "react-i18next";
import { formatNumberCL } from "../../../utils/formatters";

const ITEMS_PER_PAGE = 15;

const frequencyMap = {
  weekday: "Lunes a viernes",
  saturday: "Sábado",
  sunday: "Domingo",
};

// Cards de filtro por frecuencia (single-select; "Todas" = las tres)
const FREQ_CARDS_DEF = [
  { key: "all", labelKey: "Todas", icon: Layers, tone: "brand" },
  { key: "week", labelKey: "Lunes a viernes", icon: Calendar, tone: "info", freq: "Lunes a viernes" },
  { key: "saturday", labelKey: "Sábado", icon: Calendar, tone: "warning", freq: "Sábado" },
  { key: "sunday", labelKey: "Domingo", icon: Calendar, tone: "success", freq: "Domingo" },
];

const FREQ_TONE = { "Lunes a viernes": "info", "Sábado": "warning", "Domingo": "success" };

function iniciales(nombre) {
  return (nombre || "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";
}

export default function AdminWithdrawalsView() {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const FREQ_CARDS = FREQ_CARDS_DEF.map((c) => ({ ...c, label: t(c.labelKey) }));

  const fetchRoutes = useRoutes((state) => state.fetchRoutesBeartrack);
  const fetchClients = useRoutes((state) => state.fetchClients);
  const fetchDeliveries = useRoutes((state) => state.fetchDeliveries);
  const routes = useRoutes((state) => state.routes);
  const clients = useRoutes((state) => state.clients);
  const deliveries = useRoutes((state) => state.deliveries);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterState, setFilterState] = useState({ week: true, saturday: true, sunday: true });
  const [sortBy, setSortBy] = useState("routeName");
  const [filterDelivery, setFilterDelivery] = useState(null);
  const [filterClient, setFilterClient] = useState(null);
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);
  const [displayedRoutes, setDisplayedRoutes] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Modales (sin cambios)
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [openModalRoute, setOpenModalRoute] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openModalEditClients, setOpenModalEditClients] = useState(false);

  const observerTarget = useRef(null);
  const observerRef = useRef(null);

  const userDeliveryRole = "delivery";
  const status = "active";

  useEffect(() => {
    const logisticCompanyId = user?.logisticCompany._id || null;
    if (logisticCompanyId) {
      setLoading(true);
      Promise.all([
        fetchClients(logisticCompanyId),
        fetchDeliveries(status, userDeliveryRole, logisticCompanyId),
        fetchRoutes(),
      ]).then(() => setLoading(false));
    }
  }, [user]);

  const transformedRoutes = useMemo(() => {
    if (!routes || !Array.isArray(routes)) return [];
    return routes.map((route) => ({
      ...route,
      frequency: frequencyMap[route.frequency?.toLowerCase()] || route.frequency,
    }));
  }, [routes]);

  const sortOptions = [
    { value: "routeName", label: t("Nombre de ruta (A-Z)") },
    { value: "routeNameDesc", label: t("Nombre de ruta (Z-A)") },
    { value: "createdAtDesc", label: t("Fecha de creación (más reciente)") },
    { value: "createdAtAsc", label: t("Fecha de creación (más antigua)") },
    { value: "clientsDesc", label: t("Cantidad de clientes (mayor)") },
    { value: "clientsAsc", label: t("Cantidad de clientes (menor)") },
    { value: "deliveryAsc", label: t("Repartidor (A-Z)") },
    { value: "deliveryDesc", label: t("Repartidor (Z-A)") },
  ];

  const deliveryOptions = useMemo(() => {
    if (!Array.isArray(deliveries)) return [];
    return deliveries.map((d) => ({ value: d._id, label: `${d.name || ""} ${d.lastName || ""}`.trim() }));
  }, [deliveries]);

  const clientOptions = useMemo(() => {
    if (!Array.isArray(clients)) return [];
    return clients.map((c) => ({ value: c._id, label: c.name || t("Sin nombre") }));
  }, [clients, t]);

  // Conteos por frecuencia (para las cards)
  const freqCounts = useMemo(() => {
    const c = { all: transformedRoutes.length, week: 0, saturday: 0, sunday: 0 };
    transformedRoutes.forEach((r) => {
      if (r.frequency === "Lunes a viernes") c.week += 1;
      else if (r.frequency === "Sábado") c.saturday += 1;
      else if (r.frequency === "Domingo") c.sunday += 1;
    });
    return c;
  }, [transformedRoutes]);

  // Rutas sin repartidor asignado (deliveryManName vacío).
  const unassignedCount = useMemo(
    () => transformedRoutes.filter((r) => !(r.deliveryManName || r.deliveryMan?.name)).length,
    [transformedRoutes]
  );

  const activeFreq =
    filterState.week && filterState.saturday && filterState.sunday
      ? "all"
      : filterState.week && !filterState.saturday && !filterState.sunday
      ? "week"
      : !filterState.week && filterState.saturday && !filterState.sunday
      ? "saturday"
      : !filterState.week && !filterState.saturday && filterState.sunday
      ? "sunday"
      : "custom";

  const setFreqCard = (key) => {
    if (key === "all") setFilterState({ week: true, saturday: true, sunday: true });
    else setFilterState({ week: key === "week", saturday: key === "saturday", sunday: key === "sunday" });
  };

  const filteredRoutes = useMemo(() => {
    if (!transformedRoutes || !Array.isArray(transformedRoutes)) return [];
    let result = transformedRoutes
      .filter((route) => {
        if (!filterState.week && !filterState.saturday && !filterState.sunday) return false;
        if (filterState.week && route.frequency === "Lunes a viernes") return true;
        if (filterState.saturday && route.frequency === "Sábado") return true;
        if (filterState.sunday && route.frequency === "Domingo") return true;
        return false;
      })
      .filter((route) => {
        if (searchTerm && searchTerm.length > 0) {
          const searchString = `${route.deliveryManName || ""} ${route.deliveryManEmail || ""} ${route.routeName || ""}`.toLowerCase();
          const clientMatch = (route.clients || []).some((client) => {
            const clientName = (client.name || "").toLowerCase();
            const clientLegalName = (client.legalName || "").toLowerCase();
            return clientName.includes(searchTerm.toLowerCase()) || clientLegalName.includes(searchTerm.toLowerCase());
          });
          return searchString.includes(searchTerm.toLowerCase()) || clientMatch;
        }
        return true;
      })
      .filter((route) => (filterDelivery ? route.idDeliveryMan === filterDelivery : true))
      .filter((route) => (filterClient ? (route.clients || []).some((c) => c.id === filterClient) : true))
      .filter((route) => (onlyUnassigned ? !(route.deliveryManName || route.deliveryMan?.name) : true));

    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case "routeName": return (a.routeName || "").localeCompare(b.routeName || "");
        case "routeNameDesc": return (b.routeName || "").localeCompare(a.routeName || "");
        case "createdAtDesc": return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        case "createdAtAsc": return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
        case "clientsDesc": return (b.clients?.length || 0) - (a.clients?.length || 0);
        case "clientsAsc": return (a.clients?.length || 0) - (b.clients?.length || 0);
        case "deliveryAsc": return (a.deliveryManName || "").localeCompare(b.deliveryManName || "");
        case "deliveryDesc": return (b.deliveryManName || "").localeCompare(a.deliveryManName || "");
        default: return 0;
      }
    });
    return result;
  }, [transformedRoutes, filterState, searchTerm, filterDelivery, filterClient, sortBy, onlyUnassigned]);

  useEffect(() => {
    if (!filteredRoutes || !Array.isArray(filteredRoutes)) {
      setDisplayedRoutes([]);
      setHasMore(false);
      setCurrentPage(1);
      return;
    }
    setDisplayedRoutes(filteredRoutes.slice(0, ITEMS_PER_PAGE));
    setHasMore(filteredRoutes.length > ITEMS_PER_PAGE);
    setCurrentPage(1);
  }, [filteredRoutes]);

  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || loading) return;
    if (!filteredRoutes || !Array.isArray(filteredRoutes)) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    const startIndex = nextPage * ITEMS_PER_PAGE - ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const nextRoutes = filteredRoutes.slice(startIndex, endIndex);
    setTimeout(() => {
      setDisplayedRoutes((prev) => [...prev, ...nextRoutes]);
      setHasMore(endIndex < filteredRoutes.length);
      setCurrentPage(nextPage);
      setLoadingMore(false);
    }, 250);
  }, [loadingMore, hasMore, loading, currentPage, filteredRoutes]);

  useEffect(() => {
    if (observerRef.current && observerTarget.current) {
      observerRef.current.unobserve(observerTarget.current);
      observerRef.current = null;
    }
    if (!hasMore || loadingMore || loading) return;
    const target = observerTarget.current;
    if (!target) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) loadMore();
      },
      { rootMargin: "200px", threshold: 0.1 }
    );
    obs.observe(target);
    observerRef.current = obs;
    return () => target && obs.unobserve(target);
  }, [hasMore, loadingMore, loading, loadMore]);

  // ── Handlers (sin cambios) ──
  const handleCreateRoute = () => { setSelectedRoute(null); setOpenModalRoute(true); };
  const handleEditRoute = (route) => {
    const routeForEdit = {
      ...route,
      id: route.id || route._id,
      deliveryMan: route.deliveryMan || {
        id: route.idDeliveryMan || route.deliveryMan?.id,
        name: route.deliveryManName || route.deliveryMan?.name,
        email: route.deliveryManEmail || route.deliveryMan?.email,
      },
    };
    setSelectedRoute(routeForEdit);
    setOpenModalEdit(true);
  };
  const handleDeleteRoute = (route) => { setSelectedRoute(route); setOpenModalDelete(true); };
  const handleEditRouteClients = (route) => { setSelectedRoute(route); setOpenModalEditClients(true); };
  const handleFetchRoutes = () => { setLoading(true); fetchRoutes().then(() => setLoading(false)); };

  const hasActiveAdvancedFilters = filterDelivery || filterClient || sortBy !== "routeName" || onlyUnassigned;
  const clearAdvancedFilters = () => { setFilterDelivery(null); setFilterClient(null); setSortBy("routeName"); setOnlyUnassigned(false); };

  if (isLoading || !routes) {
    return (
      <div className="aw">
        <div className="aw-kpis">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[5.25rem] rounded-lg" />
          ))}
        </div>
        <TableSkeleton rows={8} columns={["w-40", "w-24", "w-16", "w-44", "w-20"]} />
      </div>
    );
  }

  return (
    <div className="aw">
      {/* ── Modales (idénticos) ── */}
      <Modal size="md" open={openModalRoute} setOpen={() => setOpenModalRoute(false)}
        body={<CreateRouteBeartrack closeModal={() => { setOpenModalRoute(false); handleFetchRoutes(); }} route={selectedRoute} edit={false} />} />
      <Modal size="md" open={openModalEdit} setOpen={() => setOpenModalEdit(false)}
        body={<CreateRouteBeartrack closeModal={() => { setOpenModalEdit(false); handleFetchRoutes(); }} route={selectedRoute} edit={true} />} />
      <Modal size="md" open={openModalDelete} setOpen={() => setOpenModalDelete(false)}
        body={<BlockDeleteUserBeartrack closeModal={() => { setOpenModalDelete(false); handleFetchRoutes(); }} fetch={handleFetchRoutes} route={selectedRoute} edit={true} isRoute={true} />} />
      <Modal size="lg" open={openModalEditClients} setOpen={() => setOpenModalEditClients(false)}
        body={selectedRoute && (<EditRouteClients closeModal={() => { setOpenModalEditClients(false); handleFetchRoutes(); }} routeData={selectedRoute} />)} />

      {/* ── Header ── */}
      <PageHeader
        icon={<Route className="w-6 h-6 text-fg-muted" />}
        title={t("Administrar retiros")}
        subtitle={t("Gestiona las rutas de retiro: frecuencia, clientes y repartidor asignado.")}
        actions={
          <Button variant="primary" iconLeft={<Plus size={16} />} onClick={handleCreateRoute}>
            {t("Crear nueva ruta")}
          </Button>
        }
      />

      {/* ── Cards de filtro por frecuencia ── */}
      <div className="aw-kpis">
        {FREQ_CARDS.map((c) => {
          const Icon = c.icon;
          const isActive = activeFreq === c.key;
          const count = freqCounts[c.key] ?? 0;
          return (
            <button key={c.key} className={`aw-kpi aw-kpi--${c.tone} ${isActive ? "is-active" : ""}`} onClick={() => setFreqCard(c.key)}>
              <div className="aw-kpi__top">
                <span className="aw-kpi__icon"><Icon size={16} /></span>
                <span className="aw-kpi__count">{formatNumberCL(count)}</span>
              </div>
              <div className="aw-kpi__label">{c.label}</div>
            </button>
          );
        })}
      </div>

      {/* ── Búsqueda + filtros avanzados ── */}
      <div className="aw-card aw-filters">
        <div className="aw-search">
          <Search size={16} className="aw-search__icon" />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("Buscar por ruta, repartidor o cliente…")}
          />
        </div>
        <div className="aw-field">
          <label><ArrowUpDown size={13} /> {t("Ordenar")}</label>
          <SearchDropdown options={sortOptions} selected={sortOptions.find((o) => o.value === sortBy)?.label} onChange={(v) => setSortBy(v)} placeholder={t("Ordenar por…")} />
        </div>
        <div className="aw-field">
          <label><User size={13} /> {t("Repartidor")}</label>
          <SearchDropdown options={deliveryOptions} selected={deliveryOptions.find((o) => o.value === filterDelivery)?.label || ""} onChange={(v) => setFilterDelivery(v || null)} placeholder={t("Todos")} />
        </div>
        <div className="aw-field">
          <label><Users size={13} /> {t("Cliente")}</label>
          <SearchDropdown options={clientOptions} selected={clientOptions.find((o) => o.value === filterClient)?.label || ""} onChange={(v) => setFilterClient(v || null)} placeholder={t("Todos")} />
        </div>
        <button
          type="button"
          aria-pressed={onlyUnassigned}
          onClick={() => setOnlyUnassigned((v) => !v)}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
            onlyUnassigned
              ? "border-status-warning bg-status-warning-soft text-status-warning"
              : "border-border bg-bg text-fg-muted hover:text-fg"
          }`}
          title={t("Mostrar solo rutas sin repartidor asignado")}
        >
          <UserX size={15} />
          {t("Sin repartidor")}
          <span className={`inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 text-xs font-semibold ${
            onlyUnassigned ? "bg-status-warning/20" : "bg-bg-muted text-fg-muted"
          }`}>
            {formatNumberCL(unassignedCount)}
          </span>
        </button>
        {hasActiveAdvancedFilters && (
          <button className="aw-btn aw-btn--ghost" onClick={clearAdvancedFilters}><X size={15} /> {t("Limpiar")}</button>
        )}
      </div>

      {/* ── Tabla ── */}
      {loading && displayedRoutes.length === 0 ? (
        <TableSkeleton rows={8} columns={["w-40", "w-24", "w-16", "w-44", "w-20"]} />
      ) : displayedRoutes.length === 0 ? (
        <div className="aw-card">
          <EmptyState
            icon={<Route />}
            title={t("Sin rutas")}
            description={
              searchTerm || hasActiveAdvancedFilters || activeFreq !== "all"
                ? t("No hay rutas que coincidan con los filtros.")
                : t("Aún no hay rutas de retiro creadas.")
            }
          />
        </div>
      ) : isMobile ? (
        <div className="aw-mobile-cards">
          {displayedRoutes.map((route) => {
            const clientsCount = route.clients?.length || 0;
            const dName = route.deliveryManName || route.deliveryMan?.name;
            const dEmail = route.deliveryManEmail || route.deliveryMan?.email;
            return (
              <div className="aw-mcard" key={route.id || route._id}>
                <div className="aw-mcard__top">
                  <div className="aw-route">
                    <span className="aw-route__icon"><Route size={15} /></span>
                    <span className="aw-route__name">{route.routeName || t("Sin nombre")}</span>
                  </div>
                  <span className={`aw-badge aw-badge--${FREQ_TONE[route.frequency] || "neutral"}`}>
                    {route.frequency ? t(route.frequency) : "—"}
                  </span>
                </div>
                <div className="aw-mcard__body">
                  <div className="aw-mrow">
                    <span className="aw-mrow__k">{t("Clientes")}</span>
                    <span className="aw-chip"><Users size={13} /> {clientsCount}</span>
                  </div>
                  <div className="aw-mrow">
                    <span className="aw-mrow__k">{t("Repartidor")}</span>
                    {dName ? (
                      <div className="aw-user">
                        <span className="aw-avatar">{iniciales(dName)}</span>
                        <div className="aw-user__info">
                          <div className="aw-cell-strong">{dName}</div>
                          {dEmail && <div className="aw-cell-muted">{dEmail}</div>}
                        </div>
                      </div>
                    ) : (
                      <span className="aw-cell-muted">{t("Sin asignar")}</span>
                    )}
                  </div>
                </div>
                <div className="aw-mcard__foot">
                  <div className="aw-actions">
                    <button className="aw-iconbtn" title={t("Editar clientes")} onClick={() => handleEditRouteClients(route)}><Route size={15} /></button>
                    <button className="aw-iconbtn" title={t("Editar ruta")} onClick={() => handleEditRoute(route)}><Edit size={15} /></button>
                    <button className="aw-iconbtn aw-iconbtn--danger" title={t("Eliminar")} onClick={() => handleDeleteRoute(route)}><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            );
          })}
          {hasMore && (
            <div ref={observerTarget} className="aw-observer">
              {loadingMore && (
                <div className="aw-loadingmore"><span className="aw-spinner" aria-hidden /> <span>{t("Cargando más rutas…")}</span></div>
              )}
            </div>
          )}
          {!hasMore && <div className="aw-end">{t("Se mostraron todas las rutas")}</div>}
        </div>
      ) : (
        <div className="aw-card">
          <div className="aw-list" role="table" aria-label={t("Rutas de retiro")}>
            <div className="aw-list__head" role="row">
              <div className="aw-list__hcell" role="columnheader">{t("Ruta")}</div>
              <div className="aw-list__hcell" role="columnheader">{t("Frecuencia")}</div>
              <div className="aw-list__hcell" role="columnheader">{t("Clientes")}</div>
              <div className="aw-list__hcell" role="columnheader">{t("Repartidor")}</div>
              <div className="aw-list__hcell aw-list__hcell--right" role="columnheader">{t("Acciones")}</div>
            </div>
            {displayedRoutes.map((route) => {
              const clientsCount = route.clients?.length || 0;
              const dName = route.deliveryManName || route.deliveryMan?.name;
              const dEmail = route.deliveryManEmail || route.deliveryMan?.email;
              return (
                <div className="aw-list__row" role="row" key={route.id || route._id}>
                  <div className="aw-list__cell" role="cell">
                    <div className="aw-route">
                      <span className="aw-route__icon"><Route size={15} /></span>
                      <span className="aw-route__name">{route.routeName || t("Sin nombre")}</span>
                    </div>
                  </div>
                  <div className="aw-list__cell" role="cell">
                    <span className={`aw-badge aw-badge--${FREQ_TONE[route.frequency] || "neutral"}`}>
                      {route.frequency ? t(route.frequency) : "—"}
                    </span>
                  </div>
                  <div className="aw-list__cell" role="cell">
                    <span className="aw-chip"><Users size={13} /> {clientsCount}</span>
                  </div>
                  <div className="aw-list__cell" role="cell">
                    {dName ? (
                      <div className="aw-user">
                        <span className="aw-avatar">{iniciales(dName)}</span>
                        <div className="aw-user__info">
                          <div className="aw-cell-strong">{dName}</div>
                          {dEmail && <div className="aw-cell-muted">{dEmail}</div>}
                        </div>
                      </div>
                    ) : (
                      <span className="aw-cell-muted">{t("Sin asignar")}</span>
                    )}
                  </div>
                  <div className="aw-list__cell aw-list__cell--right" role="cell">
                    <div className="aw-actions">
                      <button className="aw-iconbtn" title={t("Editar clientes")} onClick={() => handleEditRouteClients(route)}><Route size={15} /></button>
                      <button className="aw-iconbtn" title={t("Editar ruta")} onClick={() => handleEditRoute(route)}><Edit size={15} /></button>
                      <button className="aw-iconbtn aw-iconbtn--danger" title={t("Eliminar")} onClick={() => handleDeleteRoute(route)}><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              );
            })}

            {hasMore && (
              <div ref={observerTarget} className="aw-observer">
                {loadingMore && (
                  <div className="aw-loadingmore"><span className="aw-spinner" aria-hidden /> <span>{t("Cargando más rutas…")}</span></div>
                )}
              </div>
            )}
            {!hasMore && <div className="aw-end">{t("Se mostraron todas las rutas")}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
